import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import API from "../api";
import { PlusCircle, Trash2, Save } from "lucide-react";
import { useToast } from "../context/ToastContext";

export default function EditTest() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");
  const { showToast } = useToast();

  // ------------------------------------------------
  // FETCH TEST
  // ------------------------------------------------
  useEffect(() => {
    const fetchTest = async () => {
      if (!token) return;

      try {
        const { data } = await API.get(`/publictests/test/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // ⭐ Ensure type + timeLimit exist
       setTest({
  ...data,
  type: data.type || "practice",
  timeLimit: data.timeLimit || 300,
  questions: data.questions.map(q => ({
    ...q,
    media: q.media || null,
    options: (q.options || []).map(o => {
      if (typeof o === "string") {
        return { text: o, media: null };
      }
      if (!o.text) {
        return { text: "", media: null };
      }
      return {
        text: o.text,
        media: o.media || null
      };
    })
  }))
});
      } catch (err) {
        console.error("❌ Error fetching test:", err);
        setError("Failed to load test details");
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [id, token]);

  if (!token) return <Navigate to="/login" replace />;
  if (loading) return <p className="text-center mt-10">⏳ Loading test...</p>;
  if (error) return <p className="text-center mt-10 text-red-500">{error}</p>;
  if (!test) return null;

  // ------------------------------------------------
  // HANDLERS
  // ------------------------------------------------

  const handleChange = (field, value) => {
    setTest({ ...test, [field]: value });
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...test.questions];
    updated[index][field] = value;
    setTest({ ...test, questions: updated });
  };

  //const handleOptionChange = (qIndex, optIndex, value) => {
    //const updated = [...test.questions];
    //updated[qIndex].options[optIndex] = value;
    //setTest({ ...test, questions: updated });
  //};

  

  const handleRemoveOption = (qIndex, optIndex) => {
    const updated = [...test.questions];
    updated[qIndex].options.splice(optIndex, 1);
    setTest({ ...test, questions: updated });
  };
const handleAddOption = (qIndex) => {

 if (test.questions[qIndex].options.length >= 8) {
   return showToast("⚠️ Maximum 8 options allowed");
 }

 const updated = [...test.questions];
 updated[qIndex].options.push({ text: "", media: null });

 setTest({ ...test, questions: updated });
};
  const handleAddQuestion = () => {
 const updated=[
  ...test.questions,
  {
   question:"",
   media:null,
   type:"mcq",
   options:[
    {text:"",media:null},
    {text:"",media:null}
   ],
   answer: null,
   difficulty:"medium"
  }
 ];
 setTest({...test,questions:updated});
};
  const handleRemoveQuestion = (index) => {
    const updated = [...test.questions];
    updated.splice(index, 1);
    setTest({ ...test, questions: updated });
  };

  // ------------------------------------------------
  // SUBMIT
  // ------------------------------------------------
  const handleSubmit = async () => {
    // Basic validations

if (!test.title.trim()) {
  return showToast("⚠️ Title is required!");
}

if (test.questions.length === 0) {
  return showToast("⚠️ Add at least one question!");
}

if (test.questions.some(q => !q.question.trim())) {
  return showToast("⚠️ All questions must have text!");
}

if (test.questions.some(q => q.type === "mcq" && q.options.length < 2)) {
  return showToast("⚠️ MCQ must have at least 2 options!");
}

if (test.questions.some(q => q.type === "mcq" && q.options.length > 8)) {
  return showToast("⚠️ MCQ can have maximum 8 options!");
}

if (test.questions.some(q =>
  q.type === "mcq" &&
  q.options.some(opt => !opt.text.trim())
)) {
  return showToast("⚠️ All options must have text!");
}

if (test.questions.some(q => q.type === "truefalse" && !q.answer)) {
  return showToast("⚠️ Please select True or False!");
}

if (test.questions.some(q => !q.answer || q.answer === "")) {
  return showToast("⚠️ All questions must have a valid answer!");
}
    try {
      const updatedTestData = {
        title: test.title,
        description: test.description,
        type: test.type,
        visibility: test.visibility,
        timeLimit: test.type === "timed" ? Number(test.timeLimit) : undefined,
        questions:test.questions.map(q=>({
 question:q.question,
 media:q.media,
 type:q.type,
 options: q.options.map((opt, i) => ({
  key: String.fromCharCode(65 + i),
  text: opt.text,
  media: opt.media || null
})),
 answer:q.answer,
 difficulty:q.difficulty
})),
      };

      await API.put(`/publictests/test/${id}`, updatedTestData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showToast("✅ Test updated successfully!");

      navigate("/mytests");
    } catch (err) {
  console.error("FULL ERROR", err);
  console.log("STATUS", err.response?.status);
  console.log("DATA", err.response?.data);

  alert(JSON.stringify(err.response?.data, null, 2));

  showToast("Failed to update test");
}
  };

  // ------------------------------------------------
  // UI
  // ------------------------------------------------

  return (
    <div className="max-w-6xl mx-auto mt-10 p-8 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl border">
      <h2 className="text-4xl font-extrabold text-center mb-8 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">
        ✏️ Edit Your Test
      </h2>

      {/* TITLE */}
      <div className="mb-5">
        <label className="font-semibold text-gray-700 mb-2 block">Test Title</label>
        <input
          type="text"
          value={test.title}
          onChange={(e) => handleChange("title", e.target.value)}
          className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* DESCRIPTION */}
      <div className="mb-5">
        <label className="font-semibold text-gray-700 mb-2 block">Description</label>
        <textarea
          value={test.description}
          onChange={(e) => handleChange("description", e.target.value)}
          className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* TEST TYPE */}
      <div className="mb-5 bg-gray-50 p-4 rounded-xl border flex justify-between items-center">
        <label className="font-semibold text-gray-700">Test Type</label>
        <select
          value={test.type}
          onChange={(e) => handleChange("type", e.target.value)}
          className="border rounded-lg px-4 py-2"
        >
          <option value="practice">🧠 Practice</option>
          <option value="timed">⏱ Timed</option>
          <option value="exam">🚫 Exam</option>
          <option value="adaptive">🧩 Adaptive</option>
        </select>
      </div>

      {/* ⭐ TIMED TEST — TIME LIMIT INPUT */}
      {test.type === "timed" && (
        <div className="mb-6 bg-blue-50 p-4 rounded-xl border">
          <label className="font-semibold text-gray-700 block mb-2">
            Set Time Limit (in Minutes)
          </label>
          <input
            type="number"
            min={1}
            value={Math.floor(test.timeLimit / 60) || ""}
            onChange={(e) => handleChange("timeLimit", Number(e.target.value) * 60)}
            placeholder="Enter time in minutes"
            className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500"
          />

          <p className="text-sm text-gray-500 mt-2">
            (Total time for entire test — example: 10 = 10 minutes)
          </p>
        </div>
      )}

      {/* VISIBILITY */}
      <div className="mb-6 bg-gray-50 p-4 rounded-xl border flex justify-between items-center">
        <label className="font-semibold text-gray-700">Visibility</label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="visibility"
              checked={test.visibility === "private"}
              onChange={() => handleChange("visibility", "private")}
            />
            Private
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="visibility"
              checked={test.visibility === "public"}
              onChange={() => handleChange("visibility", "public")}
            />
            Public
          </label>
        </div>
      </div>

      {/* QUESTIONS */}
      {test.questions.map((q, qIndex) => (
        <div
          key={qIndex}
          className="mb-6 p-6 bg-white border rounded-xl shadow-sm"
        >
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-bold text-lg">Question {qIndex + 1}</h4>
            <button
              onClick={() => handleRemoveQuestion(qIndex)}
              className="text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <Trash2 size={18} /> Remove
            </button>
          </div>

          <input
            type="text"
            value={q.question}
            placeholder="Enter question…"
            onChange={(e) => handleQuestionChange(qIndex, "question", e.target.value)}
            className="w-full border rounded-lg px-4 py-2 mb-3"
          />
{/* Question Media Upload */}

<input
 type="file"
 accept="image/*,video/*"
 onChange={async e=>{
  const file=e.target.files[0];
  if(!file)return;

  const fd=new FormData();
  fd.append("file",file);

  const res=await API.post("/upload",fd,{
   headers:{Authorization:`Bearer ${token}`}
  });

  const updated=[...test.questions];
  updated[qIndex].media={
   url:res.data.url,
   type:file.type.startsWith("video")?"video":"image"
  };

  setTest({...test,questions:updated});
 }}
 className="my-2"
/>

{q.media && (
 q.media.type==="video" ?
 <video src={`${process.env.REACT_APP_API_BASE_URL}${q.media.url}`} controls className="h-32 mt-2"/> :
 <img src={`${process.env.REACT_APP_API_BASE_URL}${q.media.url}`} className="h-32 mt-2"/>
)}
          {/* Question type + difficulty */}
          <div className="flex gap-4 mb-4">
            <div>
              <label className="font-medium mr-2">Type:</label>
              <select
                value={q.type}
                onChange={(e) => handleQuestionChange(qIndex, "type", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="mcq">MCQ</option>
                <option value="text">Text</option>
                <option value="truefalse">True/False</option>
              </select>
            </div>

            <div>
              <label className="font-medium mr-2">Difficulty:</label>
              <select
                value={q.difficulty}
                onChange={(e) =>
                  handleQuestionChange(qIndex, "difficulty", e.target.value)
                }
                className="border rounded-lg px-3 py-2"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          {/* Options */}
          {q.type === "mcq" && (
            <div>
              {q.options.map((opt, optIndex) => (
                <div key={optIndex} className="flex items-center gap-2 mb-2">
                  <input
 value={opt.text}
 onChange={e=>{
  const updated=[...test.questions];
  updated[qIndex].options[optIndex].text=e.target.value;
  setTest({...test,questions:updated});
 }}
 className="flex-1 border rounded px-3 py-2"
/>
<input
 type="file"
 accept="image/*"
 onChange={async e=>{
  const file=e.target.files[0];
  if(!file)return;

  const fd=new FormData();
  fd.append("file",file);

  const res=await API.post("/upload",fd,{
   headers:{Authorization:`Bearer ${token}`}
  });

  const updated=[...test.questions];
  updated[qIndex].options[optIndex].media={
    url:res.data.url,
    type:"image"
  };

  setTest({...test,questions:updated});
 }}
/>

{opt.media && (
<img src={`${process.env.REACT_APP_API_BASE_URL}${opt.media.url}`} className="h-20 mt-2"/>
)}
                  <button
                    onClick={() => handleRemoveOption(qIndex, optIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✖
                  </button>
                </div>
              ))}

              <button
                onClick={() => handleAddOption(qIndex)}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <PlusCircle size={18} /> Add Option
              </button>
            </div>
          )}

          {/* Answer */}
          <div className="mt-4">

  {/* MCQ */}
  {q.type === "mcq" && (
    <select
      value={
        typeof q.answer === "string"
          ? q.answer.charCodeAt(0) - 65
          : ""
      }
      onChange={(e) => {
        const letter = String.fromCharCode(65 + Number(e.target.value));
        const updated = [...test.questions];
        updated[qIndex].answer = letter;
        setTest({ ...test, questions: updated });
      }}
      className="w-full border rounded-lg px-4 py-2"
    >
      <option value="">Select Correct Option</option>
      {q.options.map((opt, i) => (
        <option key={i} value={i}>
          Option {i + 1}
        </option>
      ))}
    </select>
  )}

  {/* TRUE / FALSE */}
  {q.type === "truefalse" && (
    <select
      value={q.answer || ""}
      onChange={(e) => {
        const updated = [...test.questions];
        updated[qIndex].answer = e.target.value;
        setTest({ ...test, questions: updated });
      }}
      className="w-full border rounded-lg px-4 py-2"
    >
      <option value="">Select Answer</option>
      <option value="true">True</option>
      <option value="false">False</option>
    </select>
  )}

  {/* TEXT */}
  {q.type === "text" && (
    <input
      type="text"
      value={q.answer || ""}
      onChange={(e) => {
        const updated = [...test.questions];
        updated[qIndex].answer = e.target.value;
        setTest({ ...test, questions: updated });
      }}
      className="w-full border rounded-lg px-4 py-2"
      placeholder="Enter correct answer"
    />
  )}

</div>
        </div>
      ))}

      {/* ADD QUESTION */}
      <div className="text-center mt-4">
        <button
          onClick={handleAddQuestion}
          className="bg-indigo-600 text-white px-6 py-3 rounded-xl shadow hover:bg-indigo-700"
        >
          <PlusCircle size={20} className="inline-block mr-2" />
          Add Question
        </button>
      </div>

      {/* SUBMIT */}
      <div className="text-center mt-10">
        <button
          onClick={handleSubmit}
          className="bg-green-600 text-white px-8 py-3 rounded-xl shadow hover:bg-green-700 text-lg"
        >
          <Save size={20} className="inline-block mr-2" />
          Update Test
        </button>
      </div>
    </div>
  );
}
